/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * OpenReplay Session Replay Service
 *
 * Provides session replay, live support (Assist), and performance profiling
 * for debugging and user experience monitoring.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import Tracker from '@openreplay/tracker'
import trackerAssist from '@openreplay/tracker-assist'
import trackerProfiler from '@openreplay/tracker-profiler'
import { createLogger } from '../utils/logger'

const logger = createLogger('openReplayService')

let tracker = null
let profilerFn = null
let isStarted = false

/**
 * Initialize OpenReplay tracker with Assist and Profiler plugins
 */
export function init() {
  const projectKey = import.meta.env.VITE_OPENREPLAY_PROJECT_KEY
  if (!projectKey) {
    logger.debug('OpenReplay: No project key configured, skipping initialization')
    return
  }

  if (tracker) {
    logger.debug('OpenReplay: Already initialized')
    return
  }

  try {
    const options = {
      projectKey,
      __DISABLE_SECURE_MODE: import.meta.env.DEV // Allow HTTP in dev
    }

    // Add ingest point if configured (for self-hosted deployments)
    const ingestPoint = import.meta.env.VITE_OPENREPLAY_INGEST_POINT
    if (ingestPoint) {
      options.ingestPoint = ingestPoint
    }

    tracker = new Tracker(options)

    // Load Assist plugin for live session support
    tracker.use(
      trackerAssist({
        callConfirm: {
          text: 'Support agent wants to start a call. Allow?'
        },
        controlConfirm: {
          text: 'Support agent wants to control your screen. Allow?'
        }
      })
    )

    // Load Profiler plugin for performance monitoring
    profilerFn = tracker.use(trackerProfiler())

    logger.debug('OpenReplay initialized')
  } catch (error) {
    logger.error('OpenReplay: Failed to initialize', error)
  }
}

/**
 * Start the OpenReplay session recording
 */
export function start() {
  if (!tracker) {
    return
  }

  if (isStarted) {
    return
  }

  try {
    tracker.start()
    isStarted = true
    logger.debug('OpenReplay session started')
  } catch (error) {
    logger.error('OpenReplay: Failed to start session', error)
  }
}

/**
 * Set the user ID for the current session
 * @param {string} userId - The user identifier (email or ID)
 */
export function setUserID(userId) {
  if (tracker && userId) {
    tracker.setUserID(userId)
  }
}

/**
 * Set metadata for the current session
 * @param {string} key - The metadata key
 * @param {string} value - The metadata value
 */
export function setMetadata(key, value) {
  if (tracker && key) {
    tracker.setMetadata(key, String(value))
  }
}

/**
 * Get a profiler wrapper for measuring function performance
 * @param {string} callName - Name to identify this function call in the profiler
 * @returns {Function} A function that wraps the target function with profiling
 *
 * @example
 * import { profiler } from './services/openReplayService'
 *
 * const expensiveCalculation = profiler('expensiveCalculation')((data) => {
 *   // ... computation
 *   return result
 * })
 */
export function profiler(callName) {
  if (profilerFn) {
    return profilerFn(callName)
  }
  // Return passthrough if profiler not initialized
  return (fn) => fn
}

/**
 * Stop the OpenReplay session recording
 */
export function stop() {
  if (tracker && isStarted) {
    tracker.stop()
    isStarted = false
  }
}

/**
 * Check if OpenReplay is initialized and running
 * @returns {boolean}
 */
export function isActive() {
  return tracker !== null && isStarted
}

export default {
  init,
  start,
  stop,
  setUserID,
  setMetadata,
  profiler,
  isActive
}
